import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'

import { LoadingScreen } from '../../../components/LoadingScreen'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

type AuthProviderProps = {
  children?: ReactNode
}

const AuthProvider: FC<AuthProviderProps> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

type AuthInitProps = {
  children?: ReactNode
}

const AuthInit: FC<AuthInitProps> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const requestUser = async (user: AuthModel) => {
      try {
        if (!didRequest.current) {
          user.page = 'refresh';
          const {data} = await getUserByToken(user)
          if(data.Status === 'Success') {
            setCurrentUser(data)
          } else {
            throw new Error(data.Status)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setLoading(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.username) {
      requestUser(auth)
    } else {
      logout()
      setLoading(false)
    }
    // eslint-disable-next-line
  }, [])

  if (loading) {
    return <LoadingScreen />
  }

  return <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
